import { RoomEvent } from 'livekit-client';
import CONSTANTS from './constants';
const events = {
  // debug mode will add event info to the console so you can be able to see what events are being triggered...
  debug: true,
  validEvents: {
    // Media related events...
    onConnected: {
      details: 'When the connection to LiveKit room is made.',
      arguments: ['participantRegistrationData'],
    },
    onDisconnected: {
      details: 'When the connection to the LiveKit room is lost.',
      arguments: ['reason'],
    },
    onConnectionError: {
      details: 'When the connection to the LiveKit room fails.',
      arguments: ['error'],
    },
    onMediaDeviceFailure: {
      details: 'When a media device fails to connect.',
      arguments: ['error'],
    },
    onEncryptionError: {
      details: 'When there is an encryption error.',
      arguments: ['error'],
    },
    onOffline: {
      details: 'When the local Internet connection goes offline.',
      arguments: [],
    },
    onOnline: {
      details: 'When the local Internet connection goes online.',
      arguments: [],
    },
    onMicrophoneShareChange: {
      details: 'When the microphone share is active or inactive.',
      arguments: ['enabled'],
    },
    onCameraShareChange: {
      details: 'When the camera share is active or inactive.',
      arguments: ['enabled'],
    },
    onScreenShareChange: {
      details: 'When the screen share is active or inactive.',
      arguments: ['enabled'],
    },
    onLastConnectionChangeUpdated: {
      details: 'When the last connection change is updated.',
      arguments: ['type', 'current', 'last'],
    },
    onLastConnectionChangeAcknowledgement: {
      details: 'When the user acknowledges a connection change (e.g., session transferred).',
      arguments: ['change'],
    },
    onUserInteraction: {
      details: 'When a user interaction is tracked in the sync document.',
      arguments: ['sessionSid', 'current', 'previous'],
    },
    onAudioChatChange: {
      details: 'When audio chat is started or ended for the session.',
      arguments: ['enabled'],
    },

    // Room Events
    onRoomReconnecting: {
      details: `The connection to the server has been interrupted and it's attempting to reconnect.`,
      liveKitEvent: RoomEvent.Reconnecting,
      context: 'room',
      arguments: [],
    },
    onRoomReconnected: {
      details: 'Reconnection has been successful',
      liveKitEvent: RoomEvent.Reconnected,
      context: 'room',
      arguments: [],
    },
    onIsRecordingChanged: {
      liveKitEvent: RoomEvent.RecordingStatusChanged,
      context: 'room',
      arguments: ['isRecording'],
    },
    onRoomDisconnected: {
      details:
        'Disconnected from room due to the room closing or unrecoverable failure',
      liveKitEvent: RoomEvent.Disconnected,
      context: 'room',
      arguments: [],
    },
    onRoomMetadataChanged: {
      details: 'Metadata associated with the room has changed',
      liveKitEvent: RoomEvent.RoomMetadataChanged,
      context: 'room',
      arguments: ['metadata'],
    },
    onRoomMetricsReceived: {
      details: 'When new metrics are received.',
      liveKitEvent: RoomEvent.MetricsReceived,
      context: 'room',
      arguments: ['metrics'],
    },

    // Remote Participant/Track events
    onRemoteParticipantConnected: {
      details: 'A RemoteParticipant joins after the local participant.',
      liveKitEvent: RoomEvent.ParticipantConnected,
      context: 'participant',
      arguments: ['Participant'],
    },
    onRemoteParticipantDisconnected: {
      details: 'A RemoteParticipant leaves',
      liveKitEvent: RoomEvent.ParticipantDisconnected,
      context: 'participant',
      arguments: ['Participant'],
    },
    onRemoteTrackPublished: {
      details:
        'A new track is published to room after the local participant has joined',
      liveKitEvent: RoomEvent.TrackPublished,
      context: 'participant',
      arguments: ['RemoteTrackPublication', 'Participant'],
    },
    onRemoteTrackUnpublished: {
      details: 'A RemoteParticipant has unpublished a track',
      liveKitEvent: RoomEvent.TrackUnpublished,
      context: 'participant',
      arguments: ['RemoteTrackPublication', 'Participant'],
    },
    onRemoteTrackMuted: {
      details: 'A track was muted',
      liveKitEvent: RoomEvent.TrackMuted,
      context: 'participant',
      arguments: ['TrackPublication', 'Participant'],
      filter: ({ Participant }) => {
        if (
          Participant?.constructor?.name ===
          CONSTANTS.LIVE_KIT.REMOTE_PARTICIPANT.CLASS_NAME
        ) {
          return true;
        } else {
          return false;
        }
      },
    },
    onRemoteTrackUnmuted: {
      details: 'A track was unmuted',
      liveKitEvent: RoomEvent.TrackUnmuted,
      context: 'participant',
      arguments: ['TrackPublication', 'Participant'],
      filter: ({ Participant }) => {
        if (
          Participant?.constructor?.name ===
          CONSTANTS.LIVE_KIT.REMOTE_PARTICIPANT.CLASS_NAME
        ) {
          return true;
        } else {
          return false;
        }
      },
    },
    onRemoteConnectionQualityChanged: {
      details: 'Connection quality was changed for a RemoteParticipant',
      liveKitEvent: RoomEvent.ConnectionQualityChanged,
      context: 'participant',
      arguments: ['connectionQuality', 'Participant'],
      filter: ({ Participant }) => {
        if (
          Participant?.constructor?.name ===
          CONSTANTS.LIVE_KIT.REMOTE_PARTICIPANT.CLASS_NAME
        ) {
          return true;
        } else {
          return false;
        }
      },
    },
    onRemoteConnectionStateChanged: {
      details: 'Connection state was changed for a RemoteParticipant',
      liveKitEvent: RoomEvent.ConnectionStateChanged,
      context: 'participant',
      arguments: ['state', 'Participant'],
      filter: ({ Participant }) => {
        if (
          Participant?.constructor?.name ===
          CONSTANTS.LIVE_KIT.REMOTE_PARTICIPANT.CLASS_NAME
        ) {
          return true;
        } else {
          return false;
        }
      },
    },
    onRemoteParticipantAttributesChanged: {
      details: `A participant's attributes were updated`,
      liveKitEvent: RoomEvent.ParticipantAttributesChanged,
      context: 'participant',
      arguments: ['changes', 'Participant'],
      filter: ({ Participant }) => {
        if (
          Participant?.constructor?.name ===
          CONSTANTS.LIVE_KIT.REMOTE_PARTICIPANT.CLASS_NAME
        ) {
          return true;
        } else {
          return false;
        }
      },
    },
    onRemoteParticipantMetadataChanged: {
      details: `The metadata was updated for a RemoteParticipant`,
      liveKitEvent: RoomEvent.ParticipantMetadataChanged,
      context: 'participant',
      arguments: ['PreviousMetaData', 'Participant'],
      filter: ({ Participant }) => {
        if (
          Participant?.constructor?.name ===
          CONSTANTS.LIVE_KIT.REMOTE_PARTICIPANT.CLASS_NAME
        ) {
          return true;
        } else {
          return false;
        }
      },
    },
    onRemoteTrackStreamStateChanged: {
      details: `Indicates if a subscribed track has been paused due to bandwidth`,
      liveKitEvent: RoomEvent.TrackStreamStateChanged,
      context: 'participant',
      arguments: ['TrackPublication', 'state', 'Participant'],
      filter: ({ Participant }) => {
        if (
          Participant?.constructor?.name ===
          CONSTANTS.LIVE_KIT.LOCAL_PARTICIPANT.CLASS_NAME
        ) {
          return true;
        } else {
          return false;
        }
      },
    },

    // Local Participant/Track events
    onLocalTrackPublished: {
      details: 'A local track was published successfully',
      liveKitEvent: RoomEvent.LocalTrackPublished,
      context: 'room',
      arguments: ['TrackPublication', 'Participant'],
    },
    onLocalTrackUnpublished: {
      details: 'A local track was unpublished',
      liveKitEvent: RoomEvent.LocalTrackUnpublished,
      context: 'room',
      arguments: ['TrackPublication', 'Participant'],
    },
    onLocalTrackMuted: {
      details: 'A track was muted',
      liveKitEvent: RoomEvent.TrackMuted,
      context: 'room',
      arguments: ['TrackPublication', 'Participant'],
      filter: ({ Participant }) => {
        if (
          Participant?.constructor?.name ===
          CONSTANTS.LIVE_KIT.LOCAL_PARTICIPANT.CLASS_NAME
        ) {
          return true;
        } else {
          return false;
        }
      },
    },
    onLocalTrackUnmuted: {
      details: 'A track was unmuted',
      liveKitEvent: RoomEvent.TrackUnmuted,
      context: 'room',
      arguments: ['TrackPublication', 'Participant'],
      filter: ({ Participant }) => {
        if (
          Participant?.constructor?.name ===
          CONSTANTS.LIVE_KIT.LOCAL_PARTICIPANT.CLASS_NAME
        ) {
          return true;
        } else {
          return false;
        }
      },
    },
    onRemoteTrackSubscribed: {
      details: 'The LocalParticipant has subscribed to a remote track',
      liveKitEvent: RoomEvent.TrackSubscribed,
      context: 'room',
      arguments: ['RemoteTrack', 'TrackPublication', 'RemoteParticipant'],
    },
    onRemoteTrackUnsubscribed: {
      details: 'A previously subscribed track has been unsubscribed',
      liveKitEvent: RoomEvent.TrackUnsubscribed,
      context: 'room',
      arguments: ['RemoteTrack', 'TrackPublication', 'RemoteParticipant'],
    },
    onRemoteTrackSubscriptionFailed: {
      details: 'There was an issue obtaining the track subscription',
      liveKitEvent: RoomEvent.TrackSubscriptionFailed,
      context: 'room',
      arguments: ['RemoteParticipant', 'trackSid', 'Error'],
    },
    onLocalConnectionQualityChanged: {
      details: 'Connection quality was changed for the LocalParticipant',
      liveKitEvent: RoomEvent.ConnectionQualityChanged,
      context: 'room',
      arguments: ['connectionQuality', 'Participant'],
      filter: ({ Participant }) => {
        if (
          Participant?.constructor?.name ===
          CONSTANTS.LIVE_KIT.LOCAL_PARTICIPANT.CLASS_NAME
        ) {
          return true;
        } else {
          return false;
        }
      },
    },
    onLocalConnectionStateChanged: {
      details: 'Connection state was changed for the LocalParticipant',
      liveKitEvent: RoomEvent.ConnectionStateChanged,
      context: 'room',
      arguments: ['state', 'Participant'],
      filter: ({ Participant }) => {
        if (
          Participant?.constructor?.name ===
          CONSTANTS.LIVE_KIT.LOCAL_PARTICIPANT.CLASS_NAME
        ) {
          return true;
        } else {
          return false;
        }
      },
    },
    onLocalParticipantAttributesChanged: {
      details: `A LocalParticipant's attributes were updated`,
      liveKitEvent: RoomEvent.ParticipantAttributesChanged,
      context: 'room',
      arguments: ['changes', 'Participant'],
      filter: ({ Participant }) => {
        if (
          Participant?.constructor?.name ===
          CONSTANTS.LIVE_KIT.LOCAL_PARTICIPANT.CLASS_NAME
        ) {
          return true;
        } else {
          return false;
        }
      },
    },
    onLocalParticipantMetadataChanged: {
      details: `A LocalParticipant's metadata was updated`,
      liveKitEvent: RoomEvent.ParticipantMetadataChanged,
      context: 'room',
      arguments: ['previousMetaData', 'Participant'],
      filter: ({ Participant }) => {
        if (
          Participant?.constructor?.name ===
          CONSTANTS.LIVE_KIT.LOCAL_PARTICIPANT.CLASS_NAME
        ) {
          return true;
        } else {
          return false;
        }
      },
    },
    onLocalTrackStreamStateChanged: {
      details: `Indicates if a subscribed track has been paused due to bandwidth`,
      liveKitEvent: RoomEvent.TrackStreamStateChanged,
      context: 'room',
      arguments: ['TrackPublication', 'state', 'Participant'],
      filter: ({ Participant }) => {
        if (
          Participant?.constructor?.name ===
          CONSTANTS.LIVE_KIT.LOCAL_PARTICIPANT.CLASS_NAME
        ) {
          return true;
        } else {
          return false;
        }
      },
    },

    // Session related events...
    onSessionLoaded: {
      details: 'When the session is loaded.',
      arguments: ['sessionData'],
    },
    onSessionReady: {
      details: 'When the session is fully initialized and the component ref is available.',
      arguments: ['sessionData'],
    },
    onSessionDetailsLoaded: {
      details: 'When the session details are loaded.',
      arguments: ['config', 'currentTab'],
    },
    onSessionApproved: {
      details:
        'When the session is approved. For proctoring this is when the proctor approves the session.',
      arguments: ['sessionSid'],
    },
    onSessionEnded: {
      details:
        'When the session is ended. This is triggered after the session has been successfully terminated.',
      arguments: ['reason', 'description', 'wasDenied'],
    },

    onSentBackToOnboardingStep: {
      details: 'When a proctor sends a user back to one or more onboarding steps.',
      arguments: ['stepIds', 'unfocusSession'],
    },

    onRedirectedToExam: {
      details: 'When a test taker has been redirected to their exam (ExamInProgress step started but not completed).',
      arguments: ['journeyStep'],
    },

    // Communication related events...
    onUnreadMessageCountChange: {
      details: 'When the unread chat message count changes.',
      arguments: ['count'],
    },

    onHandRaisedChange: {
      details: 'When a student raises or lowers their hand.',
      arguments: ['raised'],
    },

    // AI/Audit/Alerting related events...
    onFlaggingChange: {
      details: 'When the flagging status changes.',
      arguments: ['flagCount', 'flags'],
    },
    onNoiseChange: {
      details: 'When audio/noise is detected from a participant (when they start or stop speaking).',
      arguments: ['hasNoise', 'audioLevel', 'participantInfo'],
    },

    // Settings related events...
    onSettingsChange: {
      details: 'When settings are changed (audio devices, etc.). Only fires when user actively changes settings, not during initial load.',
      arguments: ['change'],
    },
  },
};

export default events;
